import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getMatchEquationText, getPlayerImageUrl } from "../../../helper/helper";
import PlayoffBadge from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/PlayoffBadge";
import { getMatchPersonDisplayName, getMatchStatusDisplayLabel } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/matchDetailDisplay";
import { playerIdFromMatchPerson } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/useOpenPlayerProfile";
import type { IMatchOfficial } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import { IcDownload, IcShare } from "../../tournamentPage/icons";
import "./resultMatch.scss";
import { bestBatter, bestBowler, bowlerOvers, buildComm, buildCommFromProgression, buildInnings, buildTeams, isMultiDay, oversText, personName, phasesFor, played, runRate, strikeRate, winnerOf } from "./derive";
import type { Inn, TeamInfo } from "./derive";
import { useResultMatchData } from "./useResultMatchData";
import { Avatar, Crest } from "./shared";
import type { Ctx, TabKey } from "./shared";
import SummaryPane from "./SummaryPane";
import ScorecardPane from "./ScorecardPane";
import CommentaryPane from "./CommentaryPane";
import PlayersPane from "./PlayersPane";
import AnalysisPane from "./AnalysisPane";
import { ViewsBadge } from "../live/parts";

const TABS: { key: TabKey; label: string }[] = [
  { key: "summary", label: "Summary" },
  { key: "scorecard", label: "Scorecard" },
  { key: "commentary", label: "Commentary" },
  { key: "players", label: "Players" },
  { key: "analysis", label: "Analysis" },
];

const TICK = (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7" fill="currentColor" fillOpacity=".18" />
    <path d="M4.8 8.3l2.1 2.1 4.3-4.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const headerOffset = () => {
  const cs = getComputedStyle(document.documentElement);
  const v = (name: string) => { const n = parseFloat(cs.getPropertyValue(name)); return Number.isFinite(n) ? n : 0; };
  return v("--top-header-height") + v("--sub-header-height");
};

const ResultMatchPage = ({ matchId }: { matchId: string }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>("summary");
  const { info, scores, scoresLoading, progression, progressionLoading, commentary, commentaryLoading, squadOne, squadTwo } = useResultMatchData(matchId, tab === "commentary");
  const match = info.data;
  const [stuck, setStuck] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const factsRef = useRef<HTMLDivElement>(null);

  const teams = useMemo(() => (match ? buildTeams(match) : null), [match]);
  const inns: Inn[] = useMemo(() => (match && teams ? buildInnings(match, scores, teams) : []), [match, scores, teams]);
  // ball-by-ball commentary (Commentary tab; also the fallback when the progression endpoint is missing)
  const phases = useMemo(() => (match ? phasesFor(match.matchType, inns[0]?.maxOvers ?? 0, isMultiDay(match, inns)) : []), [match, inns]);
  const fullComms = useMemo(() => inns.map((inn, k) => buildComm(commentary[k], inn, phases)), [inns, commentary, phases]);
  // run progression / key moments / partnerships for Summary, Scorecard and Analysis: bulk endpoint, no commentary paging
  const comms = useMemo(
    () => (progression ? inns.map((inn) => buildCommFromProgression(progression.innings.find((p) => p.inningId === inn.id), inn, phases)) : fullComms),
    [progression, inns, fullComms, phases],
  );
  const equation = useMemo(() => (match ? getMatchEquationText({ ...match, inningScorecardList: scores ?? match.inningScorecardList }) : "-"), [match, scores]);
  const winner = useMemo(() => (teams ? winnerOf(equation, teams) : null), [equation, teams]);

  // Player of the match: id (when sent) or a name match against the scorecards.
  const potm = useMemo(() => {
    if (!match) return null;
    const name = getMatchPersonDisplayName(match.playerOfTheMatch);
    if (!name || name === "-") return null;
    let id = playerIdFromMatchPerson(match.playerOfTheMatch);
    const key = name.toLowerCase();
    const byName = (first?: string | null, last?: string | null) => personName(first, last).toLowerCase() === key;
    if (!id) {
      for (const i of inns) {
        const hit = i.batting.find((b) => byName(b.playerFirstName, b.playerLastName)) ?? i.bowling.find((b) => byName(b.playerFirstName, b.playerLastName));
        if (hit) { id = hit.playerId; break; }
      }
    }
    let bat: Inn["batting"][number] | undefined;
    let bowl: Inn["bowling"][number] | undefined;
    let team: TeamInfo | undefined;
    inns.forEach((i) => {
      const b = i.batting.find((x) => (id ? x.playerId === id : byName(x.playerFirstName, x.playerLastName)));
      if (b) { bat = b; team = i.team; }
      const w = i.bowling.find((x) => (id ? x.playerId === id : byName(x.playerFirstName, x.playerLastName)));
      if (w) { bowl = w; team = team ?? i.opp; }
    });
    const o = typeof match.playerOfTheMatch === "object" ? (match.playerOfTheMatch as IMatchOfficial | null) : null;
    const squadImg = [...(squadOne?.playingSquad ?? []), ...(squadTwo?.playingSquad ?? [])].find((p) => (id ? p.playerId === id : byName(p.playerFirstName, p.playerLastName)))?.imageUri;
    const image = o?.imageUrl || o?.imageUri || o?.image || squadImg || null;
    return { name, id, bat, bowl, team, image };
  }, [match, inns, squadOne, squadTwo]);

  const openPlayer = useCallback(
    (id?: string | null) => {
      const pid = id?.trim();
      if (pid) navigate(`/tournaments/player-profile?playerId=${encodeURIComponent(pid)}`);
    },
    [navigate],
  );

  const scrollToTabs = useCallback(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const target = hero.getBoundingClientRect().bottom + window.scrollY - headerOffset();
    if (window.scrollY > target) window.scrollTo({ top: target, behavior: "smooth" });
  }, []);

  const go = useCallback(
    (t: TabKey) => {
      setTab(t);
      scrollToTabs();
    },
    [scrollToTabs],
  );

  // Show the compact scores in the tab bar once the hero facts have scrolled away.
  useEffect(() => {
    const el = factsRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const top = Math.round(headerOffset() + 54);
    const io = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting && e.boundingClientRect.top < top), { rootMargin: `-${top}px 0px 0px 0px` });
    io.observe(el);
    return () => io.disconnect();
  }, [match]);

  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = TABS.findIndex((t) => t.key === tab);
    if (e.key === "ArrowRight") { setTab(TABS[(i + 1) % TABS.length].key); e.preventDefault(); }
    if (e.key === "ArrowLeft") { setTab(TABS[(i - 1 + TABS.length) % TABS.length].key); e.preventDefault(); }
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    if (!match || pdfBusy) return;
    setPdfBusy(true);
    try {
      const { downloadScorecardPdf } = await import("./ScorecardPdf");
      await downloadScorecardPdf({ match, inns, comms, squads: [squadOne, squadTwo] });
    } catch {
      toast.error("Couldn't create the PDF. Try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: match ? `${match.teamOne.teamName} vs ${match.teamTwo.teamName}` : "Match", url: window.location.href });
      else { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); }
    } catch { /* cancelled */ }
  };

  if (info.isLoading || (scoresLoading && !scores)) {
    return <div className="mr"><div className="mr-hero" /><div className="state">Loading match...</div></div>;
  }
  if (info.isError || !match || !teams) {
    return <div className="mr"><div className="mr-hero" /><div className="state"><h3>Couldn't load this match</h3>Please refresh the page or try again in a moment.</div></div>;
  }

  /* ---------- hero data ---------- */
  const start = match.startDate ? new Date(match.startDate) : null;
  const validStart = start && !Number.isNaN(start.getTime()) ? start : null;
  const dateText = validStart ? `${validStart.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })} · ${validStart.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : "";
  const ground = (match.groundId ?? match.teamOne?.groundId ?? match.teamTwo?.groundId)?.groundName?.trim() || match.city?.trim() || "";
  // a Test / multi-day match has no meaningful over limit (the stored 450 is just a cap)
  const maxOvers = isMultiDay(match, inns) ? undefined : inns[0]?.maxOvers;
  const ended = /^(ENDED|COMPLETED|FINISHED)$/i.test(match.matchStatus ?? "");
  const title = match.leagueName?.trim() || match.tournament?.tournamentName?.trim() || "";
  const tournamentName = match.tournament?.tournamentName?.trim() || title;
  const matchTitle = `${match.teamOne.teamName} vs ${match.teamTwo.teamName}`;
  const tid = (match.tournament as { tournamentId?: string } | undefined)?.tournamentId ?? (match as { tournamentId?: string }).tournamentId ?? sessionStorage.getItem("tournamentId") ?? undefined;
  const tHref = tid ? `/tournamentDetails/${tid}` : undefined;
  const crumbLink = (label: string) =>
    tHref ? <a href={tHref} onClick={(e) => { e.preventDefault(); navigate(tHref); }}>{label}</a> : <span>{label}</span>;

  // one row per team; multi-innings matches (Test) show "250/8 & 180/4"
  const startedInns = inns.filter(played); // a next innings listed as 0/0 before a ball is bowled is not shown
  const rows: { team: TeamInfo; list: Inn[] }[] = [
    ...startedInns.filter((inn, k) => startedInns.findIndex((x) => x.team.name === inn.team.name) === k).map((inn) => ({ team: inn.team, list: startedInns.filter((x) => x.team.name === inn.team.name) })),
    ...teams.filter((t) => !startedInns.some((i) => i.team.name === t.name)).map((team) => ({ team, list: [] as Inn[] })),
  ];
  const topBat = bestBatter(inns);
  const topBowl = bestBowler(inns);
  const toss = match.tossResult?.trim();
  const equationOk = equation && equation !== "-";

  const ctx: Ctx = {
    match,
    teams,
    inns,
    comms,
    multiDay: isMultiDay(match, inns),
    phases,
    commLoading: progression ? false : progressionLoading || commentaryLoading,
    fullComms,
    fullLoading: commentaryLoading,
    squads: [squadOne, squadTwo],
    winner,
    potmId: potm?.id,
    potmName: potm?.name ?? "",
    openPlayer,
    go,
  };

  const potmStats = potm?.bat
    ? [
        [String(potm.bat.run ?? 0), "Runs"],
        [String(potm.bat.balls ?? 0), "Balls"],
        [potm.bat.strikeRate != null ? Number(potm.bat.strikeRate).toFixed(1) : strikeRate(potm.bat.run ?? 0, potm.bat.balls ?? 0), "Strike rate"],
        [`${potm.bat.fours ?? 0} / ${potm.bat.six ?? 0}`, "4s / 6s"],
      ]
    : potm?.bowl
      ? [
          [`${potm.bowl.wickets ?? 0}/${potm.bowl.runs ?? 0}`, "Figures"],
          [bowlerOvers(potm.bowl), "Overs"],
          [potm.bowl.economy != null ? Number(potm.bowl.economy).toFixed(2) : "-", "Economy"],
          [String(potm.bowl.dotBall ?? 0), "Dots"],
        ]
      : [];

  return (
    <div className="mr">
      <div className="mr-hero" ref={heroRef}>
        <section className="hero">
          <div className="hbg" aria-hidden="true"><span className="halo" /><span className="tile" /><span className="scrim-l" /><span className="scrim-b" /><span className="grain" /></div>
          <div className="wrap">
            <nav className="crumb" aria-label="Breadcrumb">
              {crumbLink("Matches")}<span>/</span>{crumbLink(tournamentName || "Tournament")}<span>/</span><b>{matchTitle}</b>
            </nav>
            <div className="hgrid">
              <div>
                <div className="meta">
                  <span className="badge">{ended ? "RESULT" : getMatchStatusDisplayLabel(match.matchStatus).toUpperCase()}</span>
                  <PlayoffBadge playoffType={match.playoffType} />
                  {dateText}
                  {maxOvers ? <><i />{maxOvers} overs</> : null}
                  {ground ? <><i />{ground}</> : null}
                  <ViewsBadge count={(match as { viewCount?: number | null }).viewCount} />
                </div>
                {title && <h1>{title}</h1>}
                <div className="teams">
                  {rows.map(({ team, list }) => {
                    const inn = list[list.length - 1];
                    const won = !!winner && winner === team;
                    const lost = !!winner && winner !== team;
                    return (
                      <div className={`trow${lost ? " lost" : ""}`} key={team.id || team.name}>
                        <Crest team={team} />
                        <div className="nm"><span>{team.name}</span>{won && <span className="wonpill">WON</span>}</div>
                        {inn ? <div className="sc">{list.map((x) => `${x.runs}/${x.wkts}`).join(" & ")}<small>({oversText(inn.legal)} ov)</small></div> : <div className="sc"><small>Did not bat</small></div>}
                      </div>
                    );
                  })}
                </div>
                {equationOk && <div className="result">{TICK}{equation}</div>}
              </div>
              <div className="hside">
                <div className="hacts">
                  <button type="button" className="hbtn" onClick={downloadPdf} disabled={pdfBusy}><IcDownload />{pdfBusy ? "Preparing PDF…" : "Download scorecard"}</button>
                  <button type="button" className="hbtn solid" onClick={share}><IcShare />Share</button>
                </div>
                {potm && (
                  <button type="button" className="potm" onClick={() => openPlayer(potm.id)} disabled={!potm.id} aria-label={`Player of the match, ${potm.name}. Open profile`}>
                    <div className="lbl"><svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1.5l1.9 4 4.4.5-3.3 3 .9 4.3L8 11.1l-3.9 2.2.9-4.3-3.3-3 4.4-.5z" /></svg>PLAYER OF THE MATCH</div>
                    <div className="who">
                      <Avatar name={potm.name} image={potm.image ? getPlayerImageUrl(potm.image) ?? null : null} />
                      <div><b>{potm.name}</b>{potm.team && <span>{potm.team.name}</span>}</div>
                      {potm.id && <svg className="go" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                    </div>
                    {potmStats.length > 0 && (
                      <div className="st">{potmStats.map(([v, l]) => <div key={l}><b>{v}</b><span>{l}</span></div>)}</div>
                    )}
                  </button>
                )}
              </div>
            </div>
            <div className="facts" ref={factsRef}>
              {toss && <div><div className="k">Toss</div><div className="v">{toss}</div></div>}
              {topBat && <div><div className="k">Top score</div><div className="v">{topBat.name} <small><span className="n">{topBat.runs}</span> ({topBat.balls})</small></div></div>}
              {topBowl && <div><div className="k">Best bowling</div><div className="v">{personName(topBowl.b.playerFirstName, topBowl.b.playerLastName)} <small><span className="n">{topBowl.b.wickets ?? 0}/{topBowl.b.runs ?? 0}</span> ({bowlerOvers(topBowl.b)})</small></div></div>}
              {inns.length > 1 && rows.length > 1 && rows.every((r) => r.list.length) && (() => { const rr = (l: Inn[]) => runRate(l.reduce((t, x) => t + x.runs, 0), l.reduce((t, x) => t + x.legal, 0)); return <div><div className="k">Run rate</div><div className="v"><span className="n">{rr(rows[0].list).toFixed(2)}</span> <small>vs</small> <span className="n">{rr(rows[1].list).toFixed(2)}</span></div></div>; })()}
            </div>
          </div>
        </section>
      </div>

      <div className="mr-sticky">
        <div className="tabsbar">
          <div className="wrap">
            <div className={`mini${stuck ? " on" : ""}`} aria-hidden="true">
              {rows.filter((r) => r.list.length).map(({ team, list }) => (
                <span className={`t${winner && winner !== team ? " l" : ""}`} key={team.id || team.name}>
                  <Crest team={team} className="c" /><span className="n">{list.map((x) => `${x.runs}/${x.wkts}`).join(" & ")}</span>
                </span>
              ))}
            </div>
            <div className="tabs" role="tablist" aria-label="Match sections" onKeyDown={onTabKey}>
              {TABS.map((t) => (
                <button key={t.key} type="button" className="tab" role="tab" id={`tab-${t.key}`} aria-controls={`pane-${t.key}`} aria-selected={tab === t.key} tabIndex={tab === t.key ? 0 : -1} onClick={() => go(t.key)}>{t.label}</button>
              ))}
            </div>
            <div className={`bar-acts${stuck ? " on" : ""}`}>
              <button type="button" className="btn" onClick={downloadPdf} disabled={pdfBusy}>{pdfBusy ? "Preparing PDF…" : "Download scorecard"}</button>
              <button type="button" className="btn primary" onClick={share}>Share</button>
            </div>
          </div>
        </div>
      </div>

      <div className="wrap main">
        {tab === "summary" && <SummaryPane ctx={ctx} />}
        {tab === "scorecard" && <ScorecardPane ctx={ctx} />}
        {tab === "commentary" && <CommentaryPane ctx={ctx} />}
        {tab === "players" && <PlayersPane ctx={ctx} />}
        {tab === "analysis" && <AnalysisPane ctx={ctx} />}
      </div>
    </div>
  );
};

export default ResultMatchPage;
